import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import {
  usersApi,
  UserFilters,
  ActivityFilters,
  UpdateUserStatusPayload,
} from '@/api/users';

/**
 * Query key factory for users
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  organizations: (id: string, params?: any) =>
    [...userKeys.detail(id), 'organizations', params] as const,
  workspaces: (id: string, params?: any) =>
    [...userKeys.detail(id), 'workspaces', params] as const,
  activity: (id: string, filters?: ActivityFilters) =>
    [...userKeys.detail(id), 'activity', filters] as const,
};

/**
 * Hook to fetch paginated list of users
 */
export function useUsers(filters: UserFilters) {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: () => usersApi.getAll(filters),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single user by ID
 */
export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => usersApi.getById(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch organizations for a user
 */
export function useUserOrganizations(id: string, params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: userKeys.organizations(id, params),
    queryFn: () => usersApi.getOrganizations(id, params),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch workspaces for a user
 */
export function useUserWorkspaces(id: string, params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: userKeys.workspaces(id, params),
    queryFn: () => usersApi.getWorkspaces(id, params),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch activity logs for a user
 */
export function useUserActivity(id: string, filters?: ActivityFilters) {
  return useQuery({
    queryKey: userKeys.activity(id, filters),
    queryFn: () => usersApi.getActivity(id, filters),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

/**
 * Hook to update user status (activate/deactivate)
 */
export function useUpdateUserStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserStatusPayload }) =>
      usersApi.updateStatus(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: `User ${variables.payload.status === 'active' ? 'activated' : 'deactivated'} successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update user status',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to restore a soft-deleted user
 */
export function useRestoreUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => usersApi.restore(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      toast({
        title: 'Success',
        description: 'User restored successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to restore user',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to send password reset email to a user
 */
export function useSendPasswordReset() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => usersApi.sendPasswordReset(id),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Password reset email sent successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to send password reset email',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to verify user's email manually
 */
export function useVerifyUserEmail() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => usersApi.verifyEmail(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      toast({
        title: 'Success',
        description: 'User email verified successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to verify email',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to fetch 2FA status for a user
 */
export function useTwoFactorStatus(id: string) {
  return useQuery({
    queryKey: [...userKeys.detail(id), 'twoFactor'],
    queryFn: () => usersApi.getTwoFactorStatus(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to reset 2FA for a user
 */
export function useResetTwoFactor() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => usersApi.resetTwoFactor(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: [...userKeys.detail(id), 'twoFactor'] });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      toast({
        title: 'Success',
        description: '2FA has been reset successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to reset 2FA',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update a user's email address
 */
export function useUpdateUserEmail() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      email,
      sendConfirmation,
    }: {
      id: string;
      email: string;
      sendConfirmation?: boolean;
    }) => usersApi.updateEmail(id, email, sendConfirmation),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast({
        title: 'Success',
        description: data.message,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update email',
        variant: 'destructive',
      });
    },
  });
}
